"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useQuery } from "@tanstack/react-query"
import { ArrowRight, RefreshCw } from "lucide-react"

import newsearch from "@/assets/newsearch.png"
import rateicon from "@/assets/rateicon.png"
import { fetchCategory, fetchTrending } from "@/lib/api"
import { parsePrice } from "@/lib/price"
import fallback from "@/public/fallback.png"
import { ICategory, ITrendingProduct } from "@/types"

export const productGridClass =
  "mx-auto grid max-w-[1294px] grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"

export function getPopularCategoryClassName(selected: boolean) {
  return `rounded-[6px] px-3 py-[6px] text-[14px] leading-[18px] font-normal transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F9E4F] ${selected ? "bg-[#1D2129] text-white" : "bg-white text-[#1D2129] hover:bg-[#E5E6EB]"}`
}

export function PopularProductCard({
  item,
  imageBackground = "muted",
}: {
  item: Omit<ITrendingProduct, "price"> & {
    price?: number | string | null
  }
  imageBackground?: "muted" | "white"
}) {
  const [imageFailed, setImageFailed] = useState(false)
  const image = Array.isArray(item.images) ? item.images[0] : undefined
  const price = parsePrice(item.price)

  return (
    <Link
      href={`/products/${item.slug}`}
      className="group relative block min-w-0 overflow-hidden rounded-[12px] bg-white transition-shadow hover:shadow-[0_4px_12px_rgba(29,33,41,0.12)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0F9E4F]"
    >
      <Image
        src={image && !imageFailed ? image : fallback}
        alt={item.title}
        width={246}
        height={246}
        unoptimized
        onError={() => setImageFailed(true)}
        className={`aspect-square h-auto w-full object-contain ${imageBackground === "white" ? "bg-white" : "bg-[#F7F8FA]"}`}
      />
      <div className="p-4">
        <p className="mb-1 truncate text-[12px] leading-4 font-medium text-[#39AF60]">
          {item.store?.name?.trim() || "---"}
        </p>
        <h3 className="line-clamp-2 h-10 text-[14px] leading-5 font-normal text-[#1D2129]">
          {item.title}
        </h3>
        <div className="mt-3 flex h-4 items-center gap-1 text-[12px] leading-4 font-normal text-[#EFAB27]">
          {item.rating ? (
            <>
              <Image
                src={rateicon}
                alt="Rating"
                width={14}
                height={14}
                className="size-[14px]"
              />
              <span>{item.rating}</span>
            </>
          ) : null}
        </div>
        <div className="mt-4 min-h-6 pr-9">
          <span className="text-[18px] leading-6 font-bold text-[#1D2129]">
            {price === null ? "$-" : `$${price.toFixed(2)}`}
          </span>
        </div>
        <span
          title="Compare prices"
          className="absolute right-4 bottom-4 size-8 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <Image
            src={newsearch}
            alt=""
            width={32}
            height={32}
            className="size-8"
          />
        </span>
      </div>
    </Link>
  )
}

export default function PopularProducts({
  initialCategories,
  initialProducts,
}: {
  initialCategories?: ICategory[]
  initialProducts?: ITrendingProduct[]
}) {
  const categoryQuery = useQuery({
    queryKey: ["home", "popular-product-categories"],
    queryFn: fetchCategory,
    initialData: initialCategories,
    staleTime: 60_000,
  })
  const productQuery = useQuery({
    queryKey: ["home", "popular-products", ""],
    queryFn: () => fetchTrending(1, "", 10),
    initialData: initialProducts,
    staleTime: 60_000,
  })
  const categories = (categoryQuery.data ?? []).filter(
    (category) =>
      category.id !== "689325269cd2ea93971fbf73" && category.name !== "All"
  )
  const filters = [{ id: "", name: "Trending" }, ...categories]

  return (
    <section
      aria-labelledby="popular-products-title"
      className="-mx-4 bg-[#F2F3F5] sm:mx-0"
    >
      <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-[72px]">
        <p className="text-[16px] leading-6 font-medium text-[#15B25C]">
          BROWSE CATALOG
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <h2
            id="popular-products-title"
            className="text-[32px] leading-10 font-bold text-[#1D2129]"
          >
            Popular Products
          </h2>
          <Link
            href="/trending-products"
            className="inline-flex items-center gap-3 text-[16px] leading-6 font-normal text-[#0F9E4F] hover:underline"
          >
            View All
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>

        <nav
          aria-label="Popular product categories"
          className="mt-4 flex flex-wrap gap-2"
        >
          {filters.map((category) => (
            <Link
              key={category.id}
              href={
                category.id
                  ? `/trending-products?id=${encodeURIComponent(category.id)}`
                  : "/trending-products"
              }
              className={getPopularCategoryClassName(category.id === "")}
            >
              {category.name}
            </Link>
          ))}
        </nav>
        {categoryQuery.isError ? (
          <div
            role="alert"
            className="mt-3 flex items-center gap-3 text-[14px] text-[#4E5969]"
          >
            Categories could not be loaded.
            <button
              type="button"
              onClick={() => categoryQuery.refetch()}
              className="inline-flex items-center gap-1 text-[#0F9E4F]"
            >
              <RefreshCw size={14} aria-hidden="true" /> Retry
            </button>
          </div>
        ) : null}

        <div
          id="popular-products-grid"
          aria-busy={productQuery.isPending}
          className="mt-8"
        >
          {productQuery.isPending ? (
            <div
              role="status"
              aria-label="Loading products"
              className={productGridClass}
            >
              {Array.from({ length: 10 }, (_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="overflow-hidden rounded-[12px] bg-white motion-safe:animate-pulse"
                >
                  <div className="aspect-square bg-[#E5E6EB]" />
                  <div className="h-[160px] p-4">
                    <div className="mb-1 h-4 w-20 rounded bg-[#F2F3F5]" />
                    <div className="h-10 rounded bg-[#F2F3F5]" />
                    <div className="mt-3 h-4 w-12 rounded bg-[#F2F3F5]" />
                    <div className="mt-4 h-6 w-20 rounded bg-[#F2F3F5]" />
                  </div>
                </div>
              ))}
            </div>
          ) : productQuery.isError ? (
            <div
              role="alert"
              className="flex min-h-[180px] flex-col items-center justify-center gap-4 text-[14px] text-[#4E5969]"
            >
              Products could not be loaded.
              <button
                type="button"
                onClick={() => productQuery.refetch()}
                className="inline-flex items-center gap-2 text-[#0F9E4F]"
              >
                <RefreshCw size={16} aria-hidden="true" /> Retry
              </button>
            </div>
          ) : productQuery.data.length === 0 ? (
            <p
              role="status"
              className="flex min-h-[180px] items-center justify-center text-[14px] text-[#4E5969]"
            >
              No products in this category yet.
            </p>
          ) : (
            <div className={productGridClass}>
              {productQuery.data.map((item) => (
                <PopularProductCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
